import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, FileDown, XCircle, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";
import "@/styles/landing.css";
import { LegalFooter } from "@/components/LegalFooter";
import { ABSCHLUSS_CONTENT_KEY } from "@/components/abschluss/useAbschlussKauffluss"; // STRIPE-ABBRUCH-01, Betreiberfreigabe 02.11.2026

// STRIPE-ABBRUCH-01, Betreiberfreigabe 02.11.2026: freigegebene Vorschau im Tab (Prüfung wie /abschluss-v2).
function vorschauImTab() {
  try {
    const wert = JSON.parse(window.sessionStorage.getItem(ABSCHLUSS_CONTENT_KEY));
    return Boolean(wert && typeof wert === "object" && Object.keys(wert).length > 0);
  } catch {
    return false;
  }
}

// D12 (Tür 1) – Rückkehrseite nach dem Stripe-Einmalkauf. Kein Konto: der Zustand
// wird ausschließlich über die session_id (Status-Polling) aufgelöst. Bei Zahlung
// erscheint der token-basierte PDF-Download. Kein späterer Wiederabruf.
const POLL_MS = 2500;
const MAX_POLLS = 40; // ~100 s

export default function EinmalkaufReturn() {
  const [params] = useSearchParams();
  const checkout = params.get("checkout");
  const sessionId = params.get("session_id");
  // state: "pending" | "paid" | "cancel" | "timeout" | "error"
  const [state, setState] = useState(checkout === "cancel" ? "cancel" : "pending");
  const [token, setToken] = useState(null);
  const timer = useRef(null);
  const polls = useRef(0);

  useEffect(() => {
    if (checkout !== "success" || !sessionId) {
      if (checkout !== "cancel") setState("error");
      return;
    }
    const tick = async () => {
      polls.current += 1;
      try {
        const res = await api.getOneTimeStatus(sessionId);
        if (res && res.paid && res.download_token) {
          setToken(res.download_token);
          setState("paid");
          if (timer.current) clearInterval(timer.current);
          return;
        }
      } catch {
        /* transient – weiter pollen */
      }
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        if (timer.current) clearInterval(timer.current);
      }
    };
    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [checkout, sessionId]);

  const downloadUrl = token ? `${API}/billing/one-time-download/${token}` : null;

  // STRIPE-ABBRUCH-01, Betreiberfreigabe 02.11.2026: Abbruch mit Vorschau im Tab → zurück auf „Wie möchten Sie weitermachen?“.
  if (state === "cancel" && vorschauImTab()) return <Navigate to="/abschluss-v2" replace />;

  return (
    <div className="lp2-root" id="top" data-testid="einmalkauf-return-page">
      <main>
        <section className="lp4-stage" style={{ minHeight: "60vh" }}>
          <div className="lp4-msg" style={{ maxWidth: 560 }}>
            {state === "pending" && (
              <>
                <Loader2 size={38} color="#3b82f6" className="spin" />
                <h2 data-testid="einmalkauf-pending">Zahlung wird bestätigt …</h2>
                <p>Einen Moment bitte – Ihre Arbeitsschutzmappe wird gleich zum Download bereitgestellt.</p>
              </>
            )}

            {state === "paid" && (
              <>
                <CheckCircle2 size={40} color="#16a34a" />
                <h2 data-testid="einmalkauf-success">Vielen Dank! Ihre Arbeitsschutzmappe ist bereit.</h2>
                <p>
                  Laden Sie Ihre CLartext-Arbeitsschutzmappe jetzt als PDF herunter. Bitte
                  speichern Sie die Datei – ein erneuter Abruf ist später nicht möglich.
                </p>
                <a
                  className="btn btn-primary btn-lg"
                  data-testid="einmalkauf-download-btn"
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FileDown size={18} /> PDF herunterladen
                </a>
              </>
            )}

            {state === "cancel" && (
              <>
                <XCircle size={38} color="#94a3b8" />
                <h2 data-testid="einmalkauf-cancel">Kauf abgebrochen</h2>
                <p>Es wurde nichts berechnet. Sie können den Kauf jederzeit erneut starten.</p>
                <Link className="btn btn-primary btn-lg" data-testid="einmalkauf-back" to="/">
                  <RefreshCw size={16} /> Zur Startseite
                </Link>
              </>
            )}

            {state === "timeout" && (
              <>
                <Loader2 size={38} color="#94a3b8" />
                <h2 data-testid="einmalkauf-timeout">Das dauert länger als erwartet</h2>
                <p>
                  Ihre Zahlung wird noch verarbeitet. Bitte aktualisieren Sie die Seite in
                  wenigen Augenblicken.
                </p>
                <button
                  className="btn btn-primary btn-lg"
                  data-testid="einmalkauf-reload"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCw size={16} /> Erneut prüfen
                </button>
              </>
            )}

            {state === "error" && (
              <>
                <XCircle size={38} color="#94a3b8" />
                <h2 data-testid="einmalkauf-invalid">Ungültiger Aufruf</h2>
                <p>Diese Seite wird nach einem Einmalkauf automatisch aufgerufen.</p>
                <Link className="btn btn-primary btn-lg" to="/">
                  <RefreshCw size={16} /> Zur Startseite
                </Link>
              </>
            )}
          </div>
        </section>
      </main>
      {/* ABO-RECHT-02, Betreiberfreigabe 27.10.2026 — Rechtslinks klein unter Status bzw. Download. */}
      <LegalFooter className="px-6 py-6" />
    </div>
  );
}
